<template>
  <v-container fluid class="edit-advertiser-page">
    <v-card class="edit-advertiser-header mb-4">
      <v-row align="center" justify="space-between" class="header">
        <v-col cols="8">
          <h1>Edit Advertiser</h1>
        </v-col>
        <v-col cols="4" class="text-right save-button-container">
          <v-btn color="primary" class="save-button" @click="saveAdvertiser">Save</v-btn>
        </v-col>
      </v-row>

      <v-row class="header-info mb-4 no-gutters" align="start" justify="start">
        <div class="field-wrapper horizontal">
          <span class="field-label">ID</span>
          <v-text-field
              class="small-input inline-input"
              v-model="advertiserInfo.advertiserId"
              readonly
              hide-details
          />
        </div>
        <div class="field-wrapper horizontal">
          <span class="field-label">Name </span>
          <v-text-field
              class="medium-input inline-input"
              v-model="advertiserInfo.advertiserName"
              hide-details
              append-icon="mdi-dots-horizontal"
          />
        </div>
        <div class="field-wrapper horizontal">
          <span class="field-label">Status </span>
          <v-select
              class="small-input inline-input"
              v-model="advertiserInfo.status"
              :items="statusOptions"
              hide-details
          />
        </div>
        <div class="field-wrapper horizontal">
          <span class="field-label">Business Developer</span>
          <v-select
              class="large-input inline-input"
              v-model="advertiserInfo.advertiserManagerId"
              :items="advertiserManagers"
              item-text="name"
              item-value="id"
              hide-details
          />
        </div>
      </v-row>
    </v-card>

    <v-card class="edit-advertiser-tabs">
      <b-tabs content-class="mt-3">
        <!-- General Tab -->
        <b-tab title="General" active>
          <v-row class="mb-4">
            <v-col cols="6">
              <div class="general-field-wrapper">
                <span class="general-field-label">Business Name or DBA</span>
                <v-text-field class="general-input" v-model="advertiserInfo.businessName" hide-details />
              </div>
            </v-col>
            <v-col cols="6">
              <div class="general-field-wrapper">
                <span class="general-field-label">Description</span>
                <v-text-field class="general-input" v-model="advertiserInfo.description" hide-details />
              </div>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col cols="6">
              <div class="general-field-wrapper">
                <span class="general-field-label">Top GEOs</span>
                <v-autocomplete
                    class="general-input"
                    v-model="selectedCountries"
                    :items="allCountries"
                    item-text="name"
                    item-value="id"
                    multiple
                    chips
                    small-chips
                    hide-details
                    :menu-props="{ maxHeight: '400px' }"
                    @change="updateSelectedCountries"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip small label close @click:close="removeCountry(index)">
                      {{ item.name }}
                    </v-chip>
                  </template>
                </v-autocomplete>

              </div>
            </v-col>
            <v-col cols="6">
              <div class="general-field-wrapper">
                <span class="general-field-label">Products Description</span>
                <v-text-field class="general-input" v-model="advertiserInfo.productDescription" hide-details />
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <div class="general-field-wrapper">
                <span class="general-field-label">Verticals</span>
                <v-autocomplete
                    class="general-input"
                    v-model="selectedVerticals"
                    :items="allVerticals"
                    item-value="id"
                    item-text="name"
                    multiple
                    chips
                    small-chips
                    hide-details
                    :menu-props="{ maxHeight: '400px' }"
                    allow-new
                    @keydown.enter.prevent="handleVerticalEnter"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip small label close @click:close="removeVertical(index)">
                      {{ item.name }}
                    </v-chip>
                  </template>
                  <template v-slot:no-data>
                    <span>Type a name and press Enter to create a new vertical</span>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>

            <v-col cols="6">
              <div class="general-field-wrapper">
                <span class="general-field-label">Other Networks</span>
                <v-autocomplete
                    class="general-input"
                    v-model="selectedNetworks"
                    :items="selectedNetworks"
                    multiple
                    placeholder="Add and/or create networks"
                    persistent-placeholder
                    chips
                    small-chips
                    hide-details
                    :menu-props="{ maxHeight: '400px' }"
                    allow-new
                    @keydown.enter.prevent="handleEnter"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                        small
                        label
                        close
                        @click:close="removeNetwork(index)"
                    >
                      {{ item }}
                    </v-chip>
                  </template>
                  <template v-slot:no-data>
                    <span>Type a name and press Enter to create a new network</span>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>
          </v-row>
          <!-- HubSpot Fields (only for platform === 2) -->
          <div v-if="platform === 2">
            <v-divider class="my-4"></v-divider>
            <h3 class="general-field-label">HubSpot Information</h3>

            <v-row class="mb-4">
              <v-col cols="6">
                <div class="general-field-wrapper">
                  <span class="general-field-label">Company Name</span>
                  <v-text-field
                    class="general-input"
                    v-model="advertiserInfo.companyName"
                    hide-details
                  />
                </div>
              </v-col>

              <v-col cols="6">
                <div class="general-field-wrapper">
                  <span class="general-field-label">Payment Method Name</span>
                  <v-text-field
                    class="general-input"
                    v-model="advertiserInfo.paymentMethodName"
                    hide-details
                  />
                </div>
              </v-col>
            </v-row>

            <v-row class="mb-4">
              <v-col cols="6">
                <div class="general-field-wrapper">
                  <span class="general-field-label">Sub Advertiser</span>
                  <v-select
                    class="general-input"
                    v-model="advertiserInfo.subAdvertiser"
                    :items="validSubAdvertiserTypes"
                    placeholder="Select sub advertiser type"
                    hide-details
                  />
                </div>
              </v-col>

            </v-row>

            <v-row class="mb-4">
              <v-col cols="6">
                <div class="general-field-wrapper">
                  <span class="general-field-label">Traffic Types</span>
                  <v-autocomplete
                    class="general-input"
                    v-model="selectedTrafficTypes"
                    :items="availableTrafficTypes.filter(t => !selectedRestrictedTrafficTypes.includes(t))"
                    multiple
                    chips
                    small-chips
                    hide-details
                    :menu-props="{ maxHeight: '400px' }"
                    @change="updateTrafficTypes"
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip small label close @click:close="removeTrafficType(index)">
                        {{ item }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>

              <v-col cols="6">
                <div class="general-field-wrapper">
                  <span class="general-field-label">Restricted Traffic Types</span>
                  <v-autocomplete
                    class="general-input"
                    v-model="selectedRestrictedTrafficTypes"
                    :items="availableTrafficTypes.filter(t => !selectedTrafficTypes.includes(t))"
                    multiple
                    chips
                    small-chips
                    hide-details
                    :menu-props="{ maxHeight: '400px' }"
                    @change="updateRestrictedTrafficTypes"
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip small label close @click:close="removeRestrictedTrafficType(index)">
                        {{ item }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>

            </v-row>
          </div>

        </b-tab>

        <!-- Contacts Tab -->
        <b-tab title="Contacts">
          <v-row>
            <v-col cols="6">
              <div class="general-field-wrapper">
                <span class="general-field-label">IM Service</span>
                <v-text-field
                    class="general-input"
                    v-model="advertiserInfo.imService"
                    hide-details
                />
              </div>
            </v-col>
            <v-col cols="6">
              <div class="general-field-wrapper">
                <span class="general-field-label">IM Contact</span>
                <v-text-field
                    class="general-input"
                    v-model="advertiserInfo.imContact"
                    hide-details
                />
              </div>
            </v-col>
          </v-row>
          <span class="general-field-label">Email(s)</span>
          <v-row v-for="(contact, index) in contacts" :key="`contact-${index}`" class="mb-4 align-center">
            <v-col cols="6">
              <div class="general-field-wrapper">
                <v-text-field
                    class="general-input"
                    v-model="contact.email"
                    placeholder="Enter email"
                    hide-details
                />
              </div>
            </v-col>
            <v-col cols="3">
              <v-radio-group
                  v-model="contact.isPrimary"
                  row
                  @change="setPrimaryContact(index)"
                  class="general-radio-group no-border"
              >
                <v-radio
                    label="Primary"
                    :value="true"
                    class="general-radio"
                />
              </v-radio-group>
            </v-col>
            <v-col cols="3" class="d-flex justify-end">
              <v-btn
                  v-if="!contact.isPrimary"
                  icon
                  @click="removeContact(index)"
                  class="remove-btn"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-btn color="primary" @click="addContact">+ Add Contact</v-btn>
        </b-tab>


        <!-- Websites Tab -->
        <b-tab title="Websites">
          <v-row class="mb-2">
            <v-col cols="5">
              <span class="general-field-label">Website</span>
            </v-col>
            <v-col cols="5">
              <span class="general-field-label">Description</span>
            </v-col>
            <v-col cols="2"></v-col>
          </v-row>

          <v-row
              v-for="(site, index) in websites"
              :key="`website-${index}`"
              class="mb-2 align-center"
          >
            <v-col cols="5">
              <v-text-field
                  v-model="site.site"
                  class="general-input"
                  placeholder="Enter site URL"
                  hide-details
              />
            </v-col>
            <v-col cols="5">
              <v-text-field
                  v-model="site.desc"
                  class="general-input"
                  placeholder="Enter description"
                  hide-details
              />
            </v-col>
            <v-col cols="2" class="d-flex justify-end">
              <v-btn
                  icon
                  @click="removeWebsite(index)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-btn color="primary" @click="addWebsite" class="mt-4">+ Add Website</v-btn>
        </b-tab>



        <!-- Payments Tab -->
        <b-tab title="Payments">
          <span class="general-field-label">Payments</span>
          <v-row class="mb-4">
            <v-col cols="6">
              <div class="general-field-wrapper">
                <span class="general-field-label">Billing Cycle</span>
                <v-select
                    v-model="advertiserInfo.billingCycle"
                    :items="billingCycleOptions"
                    class="general-input"
                    placeholder="Select billing cycle"
                    hide-details
                ></v-select>
              </div>

            </v-col>
            <v-col cols="3">
              <div class="general-field-wrapper">
                <span class="general-field-label">Margin</span>
                <v-text-field
                    v-model="advertiserInfo.margin"
                    class="general-input"
                    :rules="[rules.required, rules.float, rules.positive]"
                    suffix="%"
                    placeholder="Enter margin"
                    hide-details
                ></v-text-field>
              </div>
            </v-col>
          </v-row>

          <span class="general-field-label">Payment Methods</span>
          <v-row class="mb-4">
            <v-col cols="12">
              <div class="general-field-wrapper">
                <v-select
                    v-model="advertiserInfo.paymentMethodId"
                    :items="methods"
                    item-value="id"
                    item-text="name"
                    class="payment-general-input"
                    placeholder="Select a payment method"
                    hide-details
                ></v-select>
              </div>
            </v-col>
          </v-row>

          <div v-if="currentPaymentMethod && currentPaymentMethod.tipalti">
            <div class="tipalti-container">
              <div class="tipalti-text">
                <p>
                  This payment method is managed by our payment partner, Tipalti
                  <img class="tipalti-logo" src="@/assets/tipalti.png" alt="Tipalti Icon">. <br />
                  Log in to Tipalti to change it or contact your account manager to get access.
                </p>
              </div>
            </div>
          </div>


          <div v-if="currentPaymentMethod && !currentPaymentMethod.tipalti">
            <template v-for="pdField in (paymentDetails[currentPaymentMethod.id] || [])">
              <div class="general-field-wrapper my-field-group" :key="pdField.field">
                <span class="general-field-label">{{ pdField.label }}</span>
                <v-text-field
                    :id="`field_${pdField.field}`"
                    :name="pdField.field"
                    :rules="pdField.rules"
                    v-model="advertiserInfo.paymentDetails[pdField.field]"
                    class="payment-general-input"
                    :placeholder="'Enter ' + pdField.label"
                    hide-details
                ></v-text-field>
              </div>
            </template>
          </div>
        </b-tab>


        <!-- Postback Tab -->
        <b-tab title="Postback">
          <v-container fluid class="pa-4">
            <!-- Postback Items Table -->
            <v-data-table
                :headers="postbackTab.headers"
                :items="postbackTab.items"
                :items-per-page="5"
                hide-default-footer
                fixed-header
                class="no-border elevation-0"
            >
              <!-- Table Body -->
              <template v-slot:body="{ items }">
                <tr v-for="(item, index) in items" :key="index">
                  <!-- Event Type -->
                  <td>
                    <v-select
                        v-model="item.eventType"
                        :items="postbackTab.selectEvents"
                        item-value="eventType"
                        item-text="eventType"
                        class="border"
                        placeholder="Select Event Type"
                        hide-details
                    ></v-select>
                  </td>

                  <!-- Default -->
                  <td class="text-center default">
                    <v-radio-group
                        v-model="defaultId"
                        class="no-border default-radio-group"
                        @change="setDefault(item.id)"
                    >
                      <v-radio :value="item.id" />
                    </v-radio-group>
                    <div class="link">{{getUrl(item)}}</div>
                  </td>

                  <!-- Actions -->
                  <td class="text-center">
                    <v-btn icon @click="copyLink(item)">
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                    <v-btn :disabled="defaultId===item.id" icon @click="deletePostback(item)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <!-- Add Postback Button -->
            <v-row class="mt-4">
              <v-col cols="6">
                <v-btn class="mt-2" color="primary" @click="addNewPostback">+ Add Postback</v-btn>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Parameters Section -->
            <v-data-table
                :headers="postbackTab.paramHeaders"
                :items="postbackTab.paramItems"
                :items-per-page="5"
                hide-default-footer
                fixed-header
                class="no-border elevation-0"
            >
              <template v-slot:body="{ items }">
                <tr v-for="(item, index) in items" :key="index">
                  <!-- Key -->
                  <td>
                    <v-text-field
                        v-model="item.key_value"
                        dense
                        placeholder="Enter key"
                    ></v-text-field>
                  </td>

                  <!-- Rename To -->
                  <td>
                    <v-text-field
                        v-model="item.rename_to"
                        dense
                        placeholder="Rename to"
                        @blur="paramValidate(item, index)"
                    ></v-text-field>
                  </td>

                  <!-- Actions -->
                  <td class="text-center">
                    <v-btn icon @click="deleteParam(index)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <!-- Add Parameter Button -->
            <v-row class="mt-4">
              <v-col cols="6">
                <v-btn color="primary" @click="addNewParam">+ Add Parameter</v-btn>
              </v-col>
            </v-row>

          </v-container>
        </b-tab>




        <!-- Files Tab -->
        <b-tab title="Files">
          <v-row v-if="advertiserInfo.savedFiles && advertiserInfo.savedFiles.length" class="mb-4">
            <v-col cols="12">
              <h3 class="general-field-label">Uploaded Files</h3>
              <v-data-table
                  :headers="fileHeaders"
                  :items="advertiserInfo.savedFiles"
                  item-value="id"
                  dense
                  class="elevation-1"
                  hide-default-footer
              >
                <template v-slot:[`item.filePreview`]="{ item }">
                  <div class="file-preview-container">
                    <div v-if="isImage(item.filename)">
                      <v-img
                          :src="item.url"
                          alt="Image Preview"
                          class="file-preview"
                      ></v-img>
                    </div>
                    <v-icon
                        v-else-if="isPDF(item.filename)"
                        large
                        class="pdf-icon"
                    >
                      mdi-file-pdf-box
                    </v-icon>
                    <v-icon
                        v-else-if="isWord(item.filename)"
                        large
                        class="word-icon"
                    >
                      mdi-file-word-box
                    </v-icon>
                    <v-icon
                        v-else-if="isExcel(item.filename)"
                        large
                        class="excel-icon"
                    >
                      mdi-file-excel-box
                    </v-icon>
                    <v-icon
                        v-else-if="isZip(item.filename)"
                        large
                        class="zip-icon"
                    >
                      mdi-folder-zip
                    </v-icon>
                    <v-icon v-else large>mdi-file</v-icon>
                  </div>
                </template>


                <template v-slot:[`item.actions`]="{ item, index }">
                  <v-btn
                      small
                      icon
                      color="primary"
                      @click="downloadFile(item)"
                  >
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                  <v-btn
                      small
                      icon
                      color="primary"
                      @click="copyFileLink(item.url)"
                  >
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                  <v-btn
                      small
                      icon
                      @click="removeFile(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <template v-slot:[`item.size`]="{ item }">
                  {{ formatFileSize(item.size) }}
                </template>
              </v-data-table>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col cols="12">
              <h3 class="general-field-label">Add Files</h3>
              <div
                  @dragover.prevent="onDragOver"
                  @dragleave.prevent="onDragLeave"
                  @drop.prevent="onDrop"
              >
                <v-file-input
                    v-model="advertiserInfo.files"
                    multiple
                    label="Upload File"
                    prepend-icon="mdi-upload"
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.tar,.gz,.tar.gz,.7z,.bz2,.xz,.iso,.dmg,.tgz,.tbz,.txz"
                    show-size
                    hide-details
                    @change="onFileChange"
                ></v-file-input>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="advertiserInfo.files.length && !isUploadComplete" class="mb-4">
            <v-col cols="12" class="d-flex justify-end">
              <v-btn
                  color="primary"
                  @click="uploadFiles"
                  :disabled="isUploading"
              >
                Upload
              </v-btn>
            </v-col>
          </v-row>

          <v-row v-if="isUploading" class="mb-4">
            <v-col cols="12">
              <v-progress-linear
                  :value="uploadProgress"
                  height="8"
              ></v-progress-linear>
            </v-col>
          </v-row>

          <v-alert
              type="error"
              v-if="errorMessage"
              class="mb-4"
          >
            {{ errorMessage }}
          </v-alert>

          <v-alert
              type="success"
              v-if="isUploadComplete"
              dismissible
              class="mb-4"
              @click:close="closeAlert"
          >
            Files uploaded successfully! Please click "Save" to link the files to advertiser {{ advertiserInfo.advertiserName }}.
          </v-alert>
        </b-tab>

        <!-- History Tab -->
        <b-tab title="History">
          <v-data-table
              :headers="historyHeaders"
              :items="filteredHistory"
              :items-per-page.sync="itemsPerPage"
              :page.sync="currentPage"
              class="elevation-1"
              :footer-props="{
                'items-per-page-options': [5, 10, 25, 50, 100],
              }"
          >
          <template v-slot:top>
            <v-text-field
                v-model="search"
                label="Search History"
                class="mx-4"
                hide-details
                clearable
                clear-icon="mdi-close"
            ></v-text-field>
          </template>
          <template v-slot:[`item.dateAdded`]="{ item }">
            {{ formatDate(new Date(item.dateAdded * 1000)) }}
          </template>
          <template v-slot:[`item.logs`]="{ item }">
            <HistoryLog :logsString="item.logs" />
          </template>
          </v-data-table>
        </b-tab>
        <!-- Hubspot Tab-->


      </b-tabs>
    </v-card>
  </v-container>
</template>
<script>

import advertisers, { getAdvertiserInfo, getAdvertiserVerticals, getPresignedUrlAdvertiser } from '@/api/advertisers';
import {mapActions, mapGetters, mapState} from 'vuex';
import HistoryLog from "@/components/common/HistoryLog.vue";
import paymentDetails from "@/helpers/paymentDetails";
import {convertFileToBase64} from "@/helpers";
import offerApi from "@/api/offer";
import axios from "axios";
import affiliates from "@/api/affiliates";

export default {
  components: {HistoryLog},
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 25,
      search: '',
      historyHeaders: [
        { text: 'User (E-mail)', value: 'user' },
        { text: 'Date/Time', value: 'dateAdded' },
        { text: 'Action', value: 'action' },
        { text: 'Changes', value: 'logs' }
      ],
      showErrors: false,
      advertiserId: null,
      methods: [],
      paymentDetails,
      statusOptions: ['active', 'inactive', 'pending', 'rejected', 'suspended', 'blacklisted'],
      errorMessage: '',
      isDragging: false,
      isUploading: false,
      isUploadComplete: false,
      uploadProgress: 0,
      fileHeaders: [
        {
          text: 'File Preview',
          value: 'filePreview',
          align: 'left',
        },
        {
          text: 'File Name',
          value: 'filename',
          align: 'start',
        },
        {
          text: 'Size',
          value: 'size',
          align: 'center',
        },
        {
          text: 'Actions',
          value: 'actions',
          align: 'center',
          sortable: false,
        },
      ],
      defaultId: 0,
      postbackTab: {
        paramItems: [],
        initialParams: [],
        initialItems: [],
        deletedIds: [],
        newUrl: '',
        newEventType: '',
        paramHeaders: [
          {text: 'Advertiser Parameter to Collect', value: 'key_value', class: 'color', align: 'left', width: '30%'},
          {text: 'Rename to', value: 'rename_to', class: 'color', align: 'left', width:'25%'},
          {text: '', value: 'actions', class: 'color', align: 'center', width: '25%'}
        ],
        newParam: {
          key_value: '',
          rename_to: ''
        },
        headers: [
          { text: 'Event Type', value: 'eventType', align: 'left', width: '30%' },
          { text: 'Default', value: 'isDefault', align: 'left', width: '25%' },
          { text: '', value: 'actions', align: 'center', width: '25%' },
        ],
        items: [], // Array of postback items
        selectEvents: [
          { eventType: 'conversion' },
          { eventType: 'registration' },
          { eventType: 'installation' },
          { eventType: 'click' },
          { eventType: 'sale' },
        ],
        defaultId: null, // To track the default postback
      },
      platform: null,
      selectedTrafficTypes: [],
      selectedRestrictedTrafficTypes: [],
      availableTrafficTypes: [
        'Comparison', 'Display', 'Email', 'Exit Traffic', 'Facebook',
        'Google', 'Instagram', 'Native', 'Pop', 'SEO', 'Smartlink',
        'Survey', 'TikTok', 'SMS', 'Organic', 'Media Buy', 'Social'
      ],
      validSubAdvertiserTypes: [
        'Direct Advertiser',
        'Indirect Advertiser',
      ],

      advertiserInfo: {
        advertiserName: '',
        advertiserManagerId: null,
        status: '',
        description: '',
        tags: '',
        email: '',
        imService: '',
        imContact: '',
        margin: null,
        billingCycle: null,
        selectedVerticals: [],
        topGeos: [],
        paymentMethodId: null,
        paymentDetails: {},
        businessName: '',
        files: [],
        savedFiles: [],
        companyName: '',
        paymentMethodName: '',
        subAdvertiser: '',
        trafficTypes:'',
        restrictedTrafficTypes: '',
      },
      billingCycleOptions: ['weekly', 'semimonthly', 'monthly'],
      marginRequiredError: 'Please set the Advertiser margin before saving.',
      rules: {
        required: value => !!value || this.marginRequiredError,
        float: value => {
          const pattern = /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/
          return pattern.test(value) || 'Invalid value'
        },
        validURL: value => {
          const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
              '((([a-zA-Z0-9\\-\\.]+)\\.([a-zA-Z]{2,})|localhost)|' + // domain name and extension
              '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
              '(\\:\\d+)?(\\/[-a-zA-Z0-9%_.~+]*)*' + // port and path
              '(\\?[;&a-zA-Z0-9%_.~+=-]*)?' + // query string
              '(\\#[-a-zA-Z0-9_]*)?$', 'i'); // fragment locator
          return pattern.test(value) || 'Please enter a valid URL.';
        },
        positive: value => Number(value) >= 0 || 'Must be positive or 0'
      },
      advertiserManagers: [],
      geoOptions: [],
      networkOptions: [],
      allVerticals: [],
      allCountries: [],
      selectedCountries: [],
      selectedNetworks: [],
      selectedVerticals: [],
      originVerticals: [],
      websites: [],
      contacts: [],
      history: [],
      loading: false,
    };
  },
  async mounted() {
    this.advertiserId = this.$route.params.id;
    this.platform = await affiliates.getPlatform()
    if (!this.user[0].can('edit_advertiser')) {
      await this.$router.push('/403');
    }
    await this.loadAdvertiserInfo();
    await this.loadCountriesInfo();
    await this.loadAdvertiserVerticals();
    await this.loadManagers();
    await this.loadContacts();
    await this.loadHistory();
    await this.loadPostback();
    advertisers.getTermsAndMethods().then(result => this.methods = [...result.methods]);
  },
  computed: {
    ...mapGetters('verticals', ['getVerticals']),
    ...mapGetters('advertisers', ['getSflAdvertisersManagers']),
    ...mapGetters('countries', ['getCountries']),
    ...mapState('user', ['user']),
    currentPaymentMethod() {
      if (!this.methods.length || !this.advertiserInfo.paymentMethodId) {
        return null;
      }
      const payment = this.methods.find((method) => method.id === this.advertiserInfo.paymentMethodId);
      // console.log(`Current payment method:${JSON.stringify(payment)}`);
      return payment
    },
    filteredHistory() {
      if (!this.search) return this.history;

      const lowerSearch = this.search.toLowerCase();
      return this.history.filter(item =>
          Object.values(item).some(value =>
              String(value).toLowerCase().includes(lowerSearch)
          )
      );
    },
  },
  methods: {
    ...mapActions('alert', ['ALERT_SUCCESS', 'ALERT_ERROR', 'ALERT_INFO', 'ALERT_WARNING', 'ALERT']),
    async updatePostback() {
      const advertiserId = Number(this.advertiserId)
      try {
        this.postbackTab.items = this.postbackTab.items.map(p => {
          p.advertiserId = advertiserId;
          p.isDefault = p.isDefault ? 1 : 0;
          return p;
        });
        await advertisers.updatePostbackItems(this.postbackTab.items, this.postbackTab.deletedIds, advertiserId);

        await advertisers.updateParams(this.postbackTab.paramItems, advertiserId);
        return true
      } catch (e) {
        console.error('updatePostbackError', e);
      }

    },
    postbackChanged() {
      this.postbackTab.isUpdateDisabled = JSON.stringify(this.postbackTab.initialItems) === JSON.stringify(this.postbackTab.items)
          && JSON.stringify(this.postbackTab.initialParams) === JSON.stringify(this.postbackTab.paramItems)
    },
    setDefault(id) {
      const targetItem = this.postbackTab.items.find(item => item.id === id);

      if (!targetItem) {
        console.warn(`Item with ID ${id} not found.`);
        return;
      }
      this.postbackTab.items.forEach(item => {
        item.isDefault = item.id === id;
      });
      this.defaultId = id;
      this.postbackChanged();
    },
    deletePostback(itemToDelete) {
      this.postbackTab.items = this.postbackTab.items.filter(item => item.id !== itemToDelete.id);
      this.postbackTab.deletedIds.push(itemToDelete.id);
      this.postbackChanged();
    },
    addNewPostback() {
      const hasDefault = this.postbackTab.items.some(item => item.isDefault === true);

      const maxId = this.postbackTab.items
          .filter(item => item.id !== null)
          .map(i => i.id)
          .reduce((a, b) => Math.max(a, b), 0);

      this.postbackTab.items.push({
        id: maxId + 1,
        url: `${this.postbackTab.baseUrl}&event=${this.postbackTab.newEventType || ''}`,
        eventType: this.postbackTab.newEventType || '',
        isDefault: !hasDefault,
        status: 'new',
      });
      this.postbackTab.newUrl = '';
      this.postbackTab.newEventType = '';
      this.postbackChanged();
    },
    async copyLink(item) {
      const url = `${item.url}${item.eventType}`;
      console.log('Link copied:', url);
      this.ALERT_SUCCESS('Link Copied');
      await navigator.clipboard.writeText(url);
      setTimeout(()=>{
        this.ALERT_SUCCESS('');
      },3500)
    },
    getUrl(item) {
      return item.url.endsWith('=') ? `${item.url}${item.eventType}` : item.url;
    },
    deleteParam(index) {
      this.postbackTab.paramItems.splice(index, 1);
    },
    addNewParam() {
      this.postbackTab.paramItems.push({
        key_value: "",
        rename_to: "",
      });
    },
    paramValidate(item, index) {
      if (!item.key_value || !item.rename_to) {
        this.$dialog.notify.error("Both 'Key' and 'Rename to' fields are required.");
        return;
      }

      const isDuplicate = this.postbackTab.paramItems.some(
          (param, paramIndex) =>
              paramIndex !== index && param.key_value === item.key_value
      );
      if (isDuplicate) {
        this.$dialog.notify.error(`Parameter with key "${item.key_value}" already exists.`);
        this.postbackTab.paramItems[index].key_value = "";
      }
    },
    isImage(filename) {
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
      const extension = filename.split('.').pop().toLowerCase();
      return imageExtensions.includes(extension);
    },
    isPDF(filename) {
      return /\.pdf$/i.test(filename);
    },
    isWord(filename) {
      return /\.(doc|docx)$/i.test(filename);
    },
    isExcel(filename) {
      return /\.(xls|xlsx)$/i.test(filename);
    },
    isZip(filename) {
      return /\.zip$/i.test(filename);
    },
    formatFileSize(size) {
      if (size < 1024) return size + ' B';
      if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
      return (size / (1024 * 1024)).toFixed(1) + ' MB';
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    async onDrop(event) {
      this.isDragging = false;
      const droppedFiles = Array.from(event.dataTransfer.files);
      this.advertiserInfo.files = [...this.advertiserInfo.files, ...droppedFiles];
      this.validateFiles();
    },
    validateFiles() {
      const maxSize = 5 * 1024 * 1024;
      const invalidFiles = this.advertiserInfo.files.filter(file => file.size > maxSize);
      if (invalidFiles.length) {
        this.errorMessage = 'One or more files exceed the 5 MB size limit.';
        this.advertiserInfo.files = this.advertiserInfo.files.filter(file => file.size <= maxSize);
      } else {
        this.errorMessage = '';
      }
    },
    onFileChange() {
      if (this.advertiserInfo.files.length > 0) {
        this.isUploadComplete = false;
      }
      this.validateFiles();
    },
    closeAlert() {
      this.isUploadComplete = false;
    },

    async uploadFiles() {
      if (this.isUploading || this.isUploadComplete) return;

      this.isUploading = true;
      this.isUploadComplete = false;
      this.uploadProgress = 0;

      const totalFiles = this.advertiserInfo.files.length;

      for (let i = 0; i < totalFiles; i++) {
        const file = this.advertiserInfo.files[i];

        try {
          const presignedUrl = await getPresignedUrlAdvertiser(
              file.name,
              this.advertiserInfo.advertiserId,
              'PUT'
          );

          await axios.put(presignedUrl, file, {
            headers: {
              'Content-Type': file.type,
            },
          });
          const presignedUrlGet = await getPresignedUrlAdvertiser(
              file.name,
              this.advertiserInfo.advertiserId,
              'GET'
          );

          const presignedUrlGetAttachment = await getPresignedUrlAdvertiser(
              file.name,
              this.advertiserInfo.advertiserId,
              'GET',
              'attachment'
          );

          this.advertiserInfo.savedFiles.push({
            filename: file.name,
            size: file.size,
            url: presignedUrlGet,
            urlAttachment: presignedUrlGetAttachment
          });

          console.log('File uploaded savedFiles:',  this.advertiserInfo.savedFiles);
          this.uploadProgress = ((i + 1) / totalFiles) * 100;
        } catch (error) {
          console.error('Error uploading file:', error);
        }
      }

      this.isUploading = false;
      this.isUploadComplete = true;

      setTimeout(() => {
        this.isUploadComplete = false;
      }, 7000);
    },
    async fakeUpload(file) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    },
    clearAllFiles() {
      this.advertiserInfo.savedFiles = [];
      this.advertiserInfo.files = [];
    },
    removeFile(index) {
      this.advertiserInfo.savedFiles.splice(index, 1);
      console.log('File removed:', this.advertiserInfo.savedFiles);
    },
    copyFileLink(url) {
      navigator.clipboard.writeText(url).then(() => {
        this.$dialog.notify.success("File link copied to clipboard!");
      });
    },
    async downloadFile(file) {
      try {
        const link = document.createElement("a");
        link.href = file.urlAttachment;

        if (/^https:\/\/.*\.s3\..*\.amazonaws\.com\/.*/.test(file.urlAttachment) || file.urlAttachment.startsWith("blob:")) {
          link.download = file.filename || "download";
        } else {
          console.warn("Unknown URL format:", file.url);
          await this.$dialog.notify.error("Unsupported file URL format.");
          return;
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
        await this.$dialog.notify.error("Failed to download the file.");
      }
    },
    async loadCountriesInfo() {
      if (this.platform === 2){
        const countriesHubspot = await affiliates.getTopCountries()
        console.log(`Countries from Hubspot: ${JSON.stringify(countriesHubspot)}`);
        this.allCountries = countriesHubspot.map(country => ({
          id: country.code,
          name: country.name,
        }));
      } else {
        await this.$store.dispatch('countries/saveCountriesStore')
        this.allCountries = this.getCountries.map(country => ({
          id: country.code,
          name: country.name,
        }));
      }

    },
    async loadAdvertiserInfo() {
      this.loading = true;
      try {
        const info = await getAdvertiserInfo(this.advertiserId);
        if (info.paymentDetails) {
          const detailsArray = info.paymentDetails.split(', ');
          const detailsObject = {};
          detailsArray.forEach(detail => {
            const [key, value] = detail.split(':');
            detailsObject[key.trim()] = value.trim();
          });
          info.paymentDetails = detailsObject;
        } else {
          info.paymentDetails = {};
        }

        this.advertiserInfo = info;

        let parsedTopGeos = [];
        if (typeof info.topGeos === 'string') {
          try {
            parsedTopGeos = JSON.parse(info.topGeos);
          } catch (error) {
            console.error('Failed to parse topGeos:', error);
          }
        } else if (Array.isArray(info.topGeos)) {
          parsedTopGeos = info.topGeos;
        }

        this.selectedCountries = (parsedTopGeos || []).map(code => {
          const country = this.allCountries.find(c => c.id === code);
          return country ? { id: code, name: country.name } : { id: code, name: code };
        });

        let parsedOtherNetworks = [];
        if (typeof info.otherNetworks === 'string') {
          try {
            parsedOtherNetworks = JSON.parse(info.otherNetworks);
          } catch (error) {
            console.error('Failed to parse otherNetworks:', error);
          }
        } else if (Array.isArray(info.otherNetworks)) {
          parsedOtherNetworks = info.otherNetworks;
        }

        this.selectedNetworks = parsedOtherNetworks;
        console.log(`Parsed other networks: ${JSON.stringify(parsedOtherNetworks)}`);
        if (info.files && info.files.length) {
          this.$set(this.advertiserInfo, 'savedFiles', [...info.files]);
          this.$set(this.advertiserInfo, 'files', []);
          // console.log('Saved files:', this.advertiserInfo.savedFiles);
        } else {
          this.$set(this.advertiserInfo, 'savedFiles', []);
        }
        this.websites = info.websites ? JSON.parse(info.websites) : [];
        let parsedTrafficTypes = [];
        if (typeof info.trafficTypes === 'string') {
          try {
            parsedTrafficTypes = JSON.parse(info.trafficTypes);
          } catch (e) {
            console.error('Failed to parse trafficTypes:', e);
          }
        }
        this.selectedTrafficTypes = parsedTrafficTypes;

        let parsedRestricted = [];
        if (typeof info.restrictedTrafficTypes === 'string') {
          try {
            parsedRestricted = JSON.parse(info.restrictedTrafficTypes);
          } catch (e) {
            console.error('Failed to parse restrictedTrafficTypes:', e);
          }
        }
        this.selectedRestrictedTrafficTypes = parsedRestricted;

      } catch (error) {
        console.error('Failed to load advertiser info:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadAdvertiserVerticals() {
      this.loading = true;
      try {
        await this.$store.dispatch('verticals/saveVerticalsStore');
        this.allVerticals = this.getVerticals;
        const advertiserVerticals = await getAdvertiserVerticals({advertiser_id: this.advertiserId});
        this.selectedVerticals = advertiserVerticals.map(v => v.verticalId);
        this.originVerticals = [...this.selectedVerticals];
      } catch (error) {
        console.error('Failed to load advertiser verticals:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadManagers() {
      await this.$store.dispatch('advertisers/saveSflAdvertisersManagersStore');
      const managers = this.getSflAdvertisersManagers.map(manager => ({
        id: manager.id,
        name: `${manager.firstName} ${manager.lastName}`,
      }));
      this.advertiserManagers = managers;
    },
    async loadContacts() {
      try {
        this.contacts = await advertisers.getContacts({id: this.advertiserId});
      } catch (error) {
        console.error('Failed to load contacts:', error);
      }
    },
    async loadPostback() {
      this.postbackTab.isLoading = true;
      const res = await offerApi.getPostback(this.advertiserId);
      this.postbackTab.baseUrl = res && res.value ? res.value : '';

      try {
        this.postbackTab.items = await advertisers.getPostbacks({id:this.advertiserId});
        this.postbackTab.paramItems = await advertisers.getParams({id:this.advertiserId});

        this.postbackTab.initialParams = [...this.postbackTab.paramItems];

        this.postbackTab.initialItems = [...JSON.parse(JSON.stringify(this.postbackTab.items))];

        const initialDefaultId = this.postbackTab.items.find(i => i.isDefault === 1);
        this.defaultId = initialDefaultId ? initialDefaultId.id: 0;
        this.postbackTab.selected = initialDefaultId ? [initialDefaultId.id] : [];

      } catch (e) {
        console.error(e);
      }
      this.postbackTab.isLoading = false;
    },
    async loadHistory() {
      try {
        this.history = await advertisers.getAdvertiserHistory({id: this.advertiserId});
        // console.log('History:', this.history);
      } catch (error) {
        console.error('Failed to load contacts:', error);
      }
    },
    setPrimaryContact(selectedIndex) {
      this.contacts.forEach((contact, index) => {
        contact.isPrimary = index === selectedIndex;
      });
    },
    addNewNetwork(value) {
      if (typeof value === 'string' && value.trim() && !this.selectedNetworks.includes(value.trim())) {
        this.selectedNetworks.push(value.trim());
      }
    },
    removeNetwork(index) {
      this.selectedNetworks.splice(index, 1);
    },
    handleEnter(event) {
      const inputValue = event.target.value;
      if (inputValue) {
        this.addNewNetwork(inputValue);
        event.target.value = '';
      }
    },
    async saveAdvertiser() {
      this.loading = true;

      const validationErrors = this.validateAdvertiserData();
      if (validationErrors.length > 0) {
        this.loading = false;
        const errorMessage = validationErrors.join("\n");
        await this.$dialog.notify.error(errorMessage, {
          position: "top-right",
        });
        return;
      }

      try {
        const filesData = [];

        // console.log('Advertiser info files:', this.advertiserInfo.files);
        if (this.advertiserInfo.files && this.advertiserInfo.files.length) {
          for (let file of this.advertiserInfo.files) {
            const fileData = await convertFileToBase64(file);
            filesData.push(fileData);
          }
        }

        const paymentDetails = {};
        const selectedMethodFields = this.paymentDetails[this.advertiserInfo.paymentMethodId] || [];

        selectedMethodFields.forEach((field) => {
          paymentDetails[field.field] = this.advertiserInfo.paymentDetails?.[field.field] || "";
        });

        const topGeos = this.selectedCountries
            .filter(country => country !== null)
            .map(country => {
              if (typeof country === 'string') {
                return country;
              } else if (typeof country === 'object' && country.id) {
                return country.id;
              }
            });

        const updPostbackRes = await this.updatePostback()
        if (!updPostbackRes) {
          const error = 'Failed to update postback'
          await this.$dialog.notify.error(error, {
            position: "top-right",
          });
          this.loading = false;
          return
        }

        const updatedVerticals = this.selectedVerticals.map((vertical) => {
          if (typeof vertical === "number") {
            const existingVertical = this.allVerticals.find((v) => v.id === vertical);
            if (existingVertical) {
              return { id: existingVertical.id, name: existingVertical.name }; // Добавляем объект с ID и именем
            }
          } else if (typeof vertical === "object" && vertical.id !== null) {
            return vertical;
          } else if (typeof vertical === "string" || (typeof vertical === "object" && vertical.id === null)) {
            const newVertical = { id: null, name: vertical.name || vertical };
            this.allVerticals.push(newVertical);
            return newVertical;
          }
          return null;
        }).filter(Boolean);

        const advertiserData = {
          ...this.advertiserInfo,
          websites: this.websites,
          contacts: this.contacts,
          otherNetworks: this.selectedNetworks,
          topGeos,
          selectedVerticals:updatedVerticals,
          imService: this.advertiserInfo.imService,
          imContact: this.advertiserInfo.imContact,
          billingCycle: this.advertiserInfo.billingCycle,
          margin: this.advertiserInfo.margin,
          paymentMethodId: this.advertiserInfo.paymentMethodId,
          paymentDetails: paymentDetails,
          businessName: this.advertiserInfo.businessName,
          files: filesData,
          savedFiles: this.advertiserInfo.savedFiles,

          companyName: this.advertiserInfo.companyName || '',
          paymentMethodName: this.advertiserInfo.paymentMethodName || '',
          subAdvertiser: this.advertiserInfo.subAdvertiser || '',

          trafficTypes: JSON.stringify(this.selectedTrafficTypes || []),
          restrictedTrafficTypes: JSON.stringify(this.selectedRestrictedTrafficTypes || []),

        };

        console.log("Advertiser data to save:", JSON.stringify(advertiserData));
        const result = await advertisers.updateAdvertiserInfo(advertiserData);

        if (result && result.advertiserId) {
          await this.$dialog.notify.success("Advertiser information saved successfully", {
            position: "top-right",
            timeout: 1000,
          });
          this.originVerticals = [...this.selectedVerticals];
        } else {
          await this.$dialog.notify.error("Failed to save advertiser information");
        }
      } catch (error) {
        console.error("Error saving advertiser information:", error);
        await this.$dialog.notify.error("An error occurred while saving advertiser information");
      } finally {
        this.loading = false;
      }
    },
    removeTrafficType(index) {
      this.selectedTrafficTypes.splice(index, 1);
    },
    updateTrafficTypes() {
      this.selectedRestrictedTrafficTypes = this.selectedRestrictedTrafficTypes.filter(
        t => !this.selectedTrafficTypes.includes(t)
      );
    },
    removeRestrictedTrafficType(index) {
      this.selectedRestrictedTrafficTypes.splice(index, 1);
    },
    updateRestrictedTrafficTypes() {
      this.selectedTrafficTypes = this.selectedTrafficTypes.filter(
        t => !this.selectedRestrictedTrafficTypes.includes(t)
      );
    },

    validateAdvertiserData() {
      const errors = [];

      if (!this.advertiserInfo.advertiserName) {
        errors.push("Advertiser Name is required.");
      }
      if (!this.advertiserInfo.advertiserManagerId) {
        errors.push("Advertiser Manager is required.");
      }
      if (!this.advertiserInfo.status) {
        errors.push("Status is required.");
      }
      if (!this.advertiserInfo.businessName) {
        errors.push("Business Name is required.");
      }

      this.websites.forEach((site, index) => {
        if (!site.site) {
          errors.push(`Website URL is missing.`);
        } else {
          const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
              '((([a-zA-Z0-9\\-\\.]+)\\.([a-zA-Z]{2,})|localhost)|' + // domain name and extension
              '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
              '(\\:\\d+)?(\\/[-a-zA-Z0-9%_.~+]*)*' + // port and path
              '(\\?[;&a-zA-Z0-9%_.~+=-]*)?' + // query string
              '(\\#[-a-zA-Z0-9_]*)?$', 'i'); // fragment locator
          if (!pattern.test(site.site)) {
            errors.push(`WebsitesURL Format incorrect.`);
          }
        }
      });


      const contactEmails = new Set();
      this.contacts.forEach((contact, index) => {
        if (contact.email) {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(contact.email)) {
            errors.push(`Contact email at index ${index + 1} is invalid.`);
          } else if (contactEmails.has(contact.email)) {
            errors.push(`Duplicate contact email found at index ${index + 1}.`);
          } else {
            contactEmails.add(contact.email);
          }
        } else {
          errors.push(`Contact email at index ${index + 1} is required.`);
        }
      });
      return errors;
    },
    addWebsite() {
      this.websites.push({site: '', desc: ''});
    },
    removeWebsite(index) {
      this.websites.splice(index, 1);
    },
    addContact() {
      this.contacts.push({email: '', isPrimary: false});
    },
    removeContact(index) {
      this.contacts.splice(index, 1);
    },
    updateWebsiteField() {
      console.log('Updated websites:', this.websites);
    },
    updateSelectedVerticals(selectedItems) {
      console.log('Selected verticals updated:', selectedItems);
    },
    updateSelectedCountries(selectedItems) {
      console.log('Updated topGeos:', this.advertiserInfo.topGeos);
    },
    handleVerticalEnter(event) {
      const inputValue = event.target.value;
      if (inputValue) {
        this.addNewVertical(inputValue);
        event.target.value = '';
      }
    },
    addNewVertical(name) {
      const newVertical = { id: null, name };
      this.allVerticals.push(newVertical);
      this.selectedVerticals.push(newVertical);
    },
    removeVertical(index) {
      this.selectedVerticals.splice(index, 1);
    },
    removeCountry(index) {
      this.selectedCountries.splice(index, 1);
    },
    formatDate(date) {
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      };
      return new Date(date).toLocaleString('en-GB', options);
    }

  },
};
</script>

<style scoped>
.edit-advertiser-page {
  padding: 20px;
  background-color: #F7F8FC;
}

.edit-advertiser-header {
  padding: 20px;
  background-color: #F7F8FC !important;
  border: none !important;
  box-shadow: none !important;
}

.edit-advertiser-header::before,
.edit-advertiser-header::after {
  border: none !important;
  box-shadow: none !important;
}

.save-button-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.save-button {
  min-width: 150px;
  font-size: 16px;
  padding: 10px 20px;
  border-radius: 6px;
  background-color: #007bff;
  color: #fff;
}

.v-btn:hover {
  background-color: #0056b3;
}

::v-deep .v-text-field .v-input__control,
::v-deep .v-textarea .v-input__control,
::v-deep .v-select .v-input__control,
::v-deep .v-autocomplete .v-input__control {
  border-bottom: none !important;
  box-shadow: none !important;
}

::v-deep .v-text-field .v-input__slot::before,
::v-deep .v-text-field .v-input__slot::after,
::v-deep .v-textarea .v-input__slot::before,
::v-deep .v-textarea .v-input__slot::after,
::v-deep .v-select .v-input__slot::before,
::v-deep .v-select .v-input__slot::after,
::v-deep .v-autocomplete .v-input__slot::before,
::v-deep .v-autocomplete .v-input__slot::after {
  display: none !important;
}

::v-deep .v-input--is-focused .v-input__slot::after {
  display: none !important;
}

::v-deep .v-input__slot {
  border: 1px solid #e0e6ed !important;
  border-radius: 6px;
  background-color: #fff;
  padding: 8px 12px;
  box-shadow: none !important;
}

::v-deep .v-input__slot::before,
::v-deep .v-input__slot::after {
  transition: none !important;
}

::v-deep .v-text-field .v-label,
::v-deep .v-textarea .v-label,
::v-deep .v-select .v-label,
::v-deep .v-autocomplete .v-label {
  color: #7f98a5;
  font-weight: 500;
  font-size: 14px;
}
.uploaded-file-item {
  transition: all 0.3s;
}
.uploaded-file-item:hover {
  background-color: #f0f0f0;
}

.file-preview {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.file-input {
  border: 2px dashed #ccc;
  padding: 16px;
  border-radius: 8px;
  transition: border-color 0.3s;
}
.file-input:hover {
  border-color: #42a5f5;
}

.delete-btn:hover {
  color: #ff5252;
  transform: scale(1.1);
}

.pdf-icon {
  color: #d32f2f;
}

.word-icon {
  color: #1976d2;
}

.excel-icon {
  color: #388e3c;
}

.zip-icon {
  color: #f7a600;
  font-size: 36px;
  margin: 8px;
  transition: transform 0.2s;
}

.zip-icon:hover {
  transform: scale(1.1);
}

.file-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  width: 100px;
  overflow: hidden;
}

.file-preview-container .file-preview {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

.header-info {
  margin: 0;
  padding: 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.field-wrapper {
   display: flex;
   align-items: center;
   margin-bottom: 16px;
 }

.horizontal {
  flex-direction: row;
  margin-right: 32px;
}

.field-label {
  font-family: "Proxima Nova", "proxima-nova", Arial, Helvetica, sans-serif !important;
  font-size: 14px;
  font-weight: 900 !important;
  color: #7F98A5;
  padding-top: 15px;
  margin-right: 8px;
}

.general-field-label {
  font-family: "Proxima Nova", "proxima-nova", Arial, Helvetica, sans-serif !important;
  font-size: 14px;
  font-weight: 900 !important;
  color: #7F98A5;
  text-align: left;
  margin-bottom: 4px;
  display: block;
}

.inline-input {
  flex-shrink: 0;
  margin-left: 0;
  padding-left: 0;
}

.small-input {
  max-width: 100px;
}

.medium-input {
  max-width: 200px;
}


.general-field-wrapper {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.payment-general-input {
  width: 50%;
  max-width: 50%;
}
.general-input {
  width: 100%;
  max-width: 100%;
}

.tabs {
  width: 100%;

  .tab-content {
    max-width: 95vw;
  }

  .mt-3, .my-3 {
    margin-top: 0rem !important;
    background: #fff;
    padding: 20px;
    border-left: 2px solid #E3EEF4;
    border-right: 2px solid #E3EEF4;
    border-bottom: 2px solid #E3EEF4;
    border-radius: 0px 0px 0px 4px;
  }

  .nav-tabs {
    border-bottom: 2px solid #E3EEF4;
  }

  .nav-tabs .nav-link.active,
  .nav-tabs .nav-item.show .nav-link {
    font-size: 16px;
    font-weight: 700;
    background-color: #fff;
    border: 2px solid #E3EEF4;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
  }

  .nav-tabs {
    .nav-link {
      &:not(.active) {
        color: #7F98A5;
        font-size: 16px;
        font-weight: 500;
        background-color: #F7F8FC;
        border: none;
        box-shadow: none;
        border-radius: 0;
      }

      &.active {
        color: var(--v-primary-base);
        font-size: 16px;
        font-weight: 600;
        border: none;
        box-shadow: none;
        border-radius: 0;
      }
    }
  }


  .nav-tabs .nav-item {
    flex: 1;
  }

  .nav-tabs .nav-link:not(.active) {
    border-top: none;
  }

  .nav-link {
    margin-left: 0px;
    margin-bottom: -2px;
    padding: 10px 5rem;
  }
}
.no-border {
  border: none !important;
  box-shadow: none !important;
  .v-input__slot {
    border: none !important;
    box-shadow: none !important;
  }
}
.border {
  border: 1px solid #e0e6ed !important;
  padding-top: 0;
}
.align-center {
  vertical-align: middle;
}

.form-container {
  border: 2px solid #ccc;
  width: 50%;
  padding: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  box-sizing: border-box;
}

.method-specific-form {
  font-size: 18px;
  color: red;
  font-weight: bold;
  text-align: center;
  margin: 0;
}

.tipalti-container {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  margin-top: 12px;
}

.tipalti-logo {
  width: 22px;
}

.tipalti-text {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
}

.default {
  display: flex;
  align-items: center;
  gap: 16px;
}

.link {
  max-width: 800px !important;
  text-align: left !important;
}

</style>







